/*
tslint:disable:import-spacing no-inferrable-types
variable-name align no-trailing-whitespace component-class-suffix
no-string-literal component-selector
*/

import {ICommand} from '@/util/i.command';
import {Logger}   from '@/util/logger';

/**
 *
 * @class UnDoRedo
 *
 * @brief this is our undo / redo history manager.  An instance could be used in
 * individual panels or for the application as a whole.  Implementors of ICommand
 * (reversible command objects) are used for any and all actions, so that undo /
 * redo buttons can be linked to undo / redo actions in this controller.  Essentially,
 * when reversible commands are created they are given enough information to be able
 * to undo...what hte command is doing.
 *
 * There is a nice tutorial over here:
 *
 * https://www.typescriptlang.org/docs/handbook/interfaces.html
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      June 2020
 *
 */

export class UnDoRedo {

  /** the undo history */
  private unDoCommands: ICommand[] = [];

  /** the redo history (cleared on any new undo) */
  private reDoCommands: ICommand[] = [];

  private         name: string     = 'UnDoRedo';

  /** standard constructor */
  constructor(
    name: string = 'UnDoRedo'
  ) {

    this.name = name;
  }

  /** fetch the number of possible undo commands */
  public undoSize(): number {

    return this.unDoCommands.length;
  }

  /** fetch the number of possible redo commands */
  public redoSize(): number {

    return this.reDoCommands.length;
  }

  /** track a new reversible action (it as already executed) */
  public  insert(cmd: ICommand): void {

    Logger.debug(`[${this.name}] insert cmd: ${cmd.asString()}`);

    /* add new undo history */

    this.unDoCommands.push(cmd);

    /* put a cap on history so they can't just exaust memory with an all day session */

    if (this.unDoCommands.length > 50) {

      this.unDoCommands.shift();
    }

    /* clear out the redo history */

    this.reDoCommands = [];
  }

  /** do some number of the redo actions (undo-undo) */
  public redo(levels: number = 1): void {

    /* walk through the redo actions ... */

    for (let i = 1; i <= levels; i++) {

      if (this.reDoCommands.length === 0) {

        break;
      }

      /* consume a redo ... */

      const cmd: ICommand  = this.reDoCommands.pop();

      /* do it */

      Logger.debug(`[${this.name}] redoing cmd: ${cmd.asString()}`);

      cmd.execute();

      /* add to undo, since we just did it */

      this.unDoCommands.push(cmd);

    } /* end of redo walk */

  }

  /** do some number of the undo actions */
  public undo(levels: number = 1): void {

    for (let i = 1; i <= levels; i++) {

      if (this.unDoCommands.length === 0) {

        break;
      }

      /* consume an undo ... */

      const  cmd: ICommand = this.unDoCommands.pop();

      /* reverse that command */

      Logger.debug(`[${this.name}] undoing cmd: ${cmd.asString()}`);

      cmd.unExecute();

      /* since we just un-did it, track it as a redoable action */

      this.reDoCommands.push(cmd);

    } /* end of undo walk */

  }

}
